import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { 
  ArrowLeft,
  Package,
  User,
  MapPin,
  Phone,
  Mail,
  Truck,
  FileText,
  ChevronDown,
  Copy,
  Archive,
  XCircle,
  Eye,
  Send,
  Workflow,
  Search,
  Loader2,
  Calendar,
  Tag,
  CreditCard,
  Percent
} from "lucide-react";
import { toast } from "sonner";

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      setOrder(response.data);
    } catch (error) {
      console.error("Error fetching order:", error);
      toast.error("Order not found");
      navigate("/orders");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Unfulfilled":
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Unfulfilled</Badge>;
      case "Fulfilled":
        return <Badge className="bg-green-100 text-green-800 border-green-200">Fulfilled</Badge>;
      case "Waybill Created":
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Waybill Created</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#16a34a]" />
      </div>
    );
  }

  if (!order) return null;

  const shipping = order.shipping_address;

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate("/orders")}
            className="text-slate-600"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Orders
          </Button>
          <Separator orientation="vertical" className="h-6" />
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              #{order.order_number}
            </h1>
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">{order.payment_status}</Badge>
            {getStatusBadge(order.status)}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" className="border-slate-200">
            Refund
          </Button>
          <Button variant="outline" className="border-slate-200">
            Edit
          </Button>
          <Button variant="outline" className="border-slate-200">
            Print
          </Button>
          
          {/* More Actions Dropdown - Shopify Style */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="bg-slate-800 hover:bg-slate-700 text-white" data-testid="more-actions-btn">
                More actions
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 p-1">
              <div className="px-2 py-1.5">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input 
                    type="text" 
                    placeholder="Search actions" 
                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-[#16a34a]"
                  />
                </div>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="cursor-pointer">
                <Copy className="w-4 h-4 mr-3 text-slate-500" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer">
                <XCircle className="w-4 h-4 mr-3 text-slate-500" />
                Cancel order
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer">
                <Archive className="w-4 h-4 mr-3 text-slate-500" />
                Archive
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="cursor-pointer">
                <Eye className="w-4 h-4 mr-3 text-slate-500" />
                View order status page
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer">
                <FileText className="w-4 h-4 mr-3 text-slate-500" />
                View the invoice
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer">
                <Send className="w-4 h-4 mr-3 text-slate-500" />
                Send invoice to the customer
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="cursor-pointer">
                <Workflow className="w-4 h-4 mr-3 text-slate-500" />
                Run Flow automation
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer">
                <Search className="w-4 h-4 mr-3 text-slate-500" />
                Search Flow runs
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {/* First Freight Waybill Action - Highlighted */}
              <DropdownMenuItem 
                className="cursor-pointer bg-orange-50 hover:bg-orange-100 text-[#16a34a] font-medium"
                onClick={() => navigate(`/orders/${order.id}/waybill`)}
                data-testid="create-waybill-action"
              >
                <Truck className="w-4 h-4 mr-3" />
                Create Waybill
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <p className="text-sm text-slate-500 mb-6">
        <Calendar className="w-4 h-4 inline mr-1" />
        {order.order_date} from Online Store
      </p>

      <div className="grid grid-cols-3 gap-6">
        {/* Main Content - 2 cols */}
        <div className="col-span-2 space-y-6">
          {/* Fulfillment Card */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100 pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {getStatusBadge(order.status)}
                  <span className="text-slate-600 text-sm">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    {order.warehouse_name || "Malfurn CPT"}
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {/* Shipping Method */}
              <div className="flex items-center gap-2 mb-4 text-sm text-slate-600">
                <Truck className="w-4 h-4" />
                <span className="font-medium">{order.shipping_method}</span>
                <Separator orientation="vertical" className="h-4" />
                <span>Volumetric shipping</span>
              </div>

              {/* Line Items */}
              <div className="space-y-4">
                {order.line_items.map((item, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                    <div className="w-16 h-16 bg-white border border-slate-200 rounded-lg flex items-center justify-center">
                      <Package className="w-8 h-8 text-slate-400" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{item.name}</p>
                      <p className="text-sm text-slate-500">{item.variant}</p>
                      <p className="text-xs text-slate-400 font-mono mt-1">{item.sku}</p>
                      {order.discount_code && (
                        <div className="flex items-center gap-1 mt-2">
                          <Tag className="w-3 h-3 text-slate-400" />
                          <span className="text-xs text-slate-500">{order.discount_code} (-{Math.round((order.discount / order.subtotal) * 100)}%)</span>
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-400 line-through">R{(item.price * 1.18).toLocaleString()}</p>
                      <p className="font-medium text-slate-900">R{item.price.toLocaleString()}</p>
                      <p className="text-sm text-slate-500">× {item.quantity}</p>
                      <p className="font-semibold text-slate-900 mt-1">R{(item.price * item.quantity).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Button */}
              {order.status === "Unfulfilled" && (
                <div className="mt-6 flex justify-end">
                  <Button 
                    className="bg-[#16a34a] hover:bg-[#15803d] text-white"
                    onClick={() => navigate(`/orders/${order.id}/waybill`)}
                    data-testid="create-waybill-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Create Waybill
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment Card */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100 pb-4">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-600" />
                <CardTitle className="text-base font-semibold text-slate-900">Paid</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Subtotal</span>
                  <span className="text-slate-600">{order.line_items.length} item</span>
                  <span className="font-medium text-slate-900">R{order.subtotal.toLocaleString()}</span>
                </div>
                {order.discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Discount</span>
                    <span className="text-slate-500">{order.discount_code}</span>
                    <span className="font-medium text-red-600">-R{order.discount.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Shipping</span>
                  <span className="text-slate-500">{order.shipping_method} ({order.line_items.reduce((acc, item) => acc + item.weight, 0)} kg)</span>
                  <span className="font-medium text-slate-900">R{order.shipping_cost.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Taxes</span>
                  <span className="text-slate-500">VAT 15% (Included)</span>
                  <span className="font-medium text-slate-900">R{order.tax.toLocaleString()}</span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="font-semibold text-slate-900">Total</span>
                  <span className="font-bold text-lg text-slate-900">R{order.total.toLocaleString()}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - 1 col */}
        <div className="space-y-6">
          {/* Notes */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-700">Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500">{order.notes || "No notes from customer"}</p>
            </CardContent>
          </Card>

          {/* Customer */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-700">Customer</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-slate-500" />
                </div>
                <div>
                  <p className="font-medium text-[#16a34a] hover:underline cursor-pointer">{order.customer_name}</p>
                  <p className="text-xs text-slate-500">1 order</p>
                </div>
              </div>
              
              <Separator />
              
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Contact information</p>
                <div className="space-y-2">
                  <a href={`mailto:${order.customer_email}`} className="flex items-center gap-2 text-sm text-[#16a34a] hover:underline">
                    <Mail className="w-4 h-4" />
                    {order.customer_email}
                  </a>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Phone className="w-4 h-4 text-slate-400" />
                    {order.customer_phone}
                  </div>
                </div>
              </div>
              
              <Separator />
              
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Shipping address</p>
                <div className="text-sm text-slate-600 space-y-1">
                  <p className="font-medium text-slate-900">{order.customer_name}</p>
                  <p>{shipping.address1}</p>
                  {shipping.address2 && <p>{shipping.address2}</p>}
                  <p>{shipping.city}</p>
                  <p>{shipping.province}</p>
                  <p>{shipping.postal_code}</p>
                  <p>{shipping.country}</p>
                  <p className="text-slate-500">{order.customer_phone}</p>
                </div>
                <a href="#" className="text-sm text-[#16a34a] hover:underline mt-2 block">View map</a>
              </div>
            </CardContent>
          </Card>

          {/* Tags */}
          {order.tags && order.tags.length > 0 && (
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-700">Tags</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {order.tags.map((tag, index) => (
                    <Badge key={index} variant="outline" className="bg-slate-50">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetailPage;
